import { useState, useRef, useEffect, useMemo } from "react";

interface SimpleMultiSelectProps {
  label?: string;
  values: string[];
  options: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  creatable?: boolean;
}

export default function SimpleMultiSelect({
  label,
  values,
  options,
  onChange,
  placeholder = "Select...",
  required = false,
  disabled = false,
  hideLabel = false,
  creatable = false,
}: SimpleMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, search]);

  const exactMatch =
    search.trim() && options.some((o) => o.toLowerCase() === search.trim().toLowerCase());
  const canCreate = creatable && search.trim() && !exactMatch && !values.includes(search.trim());

  const toggleValue = (val: string) => {
    if (values.includes(val)) {
      onChange(values.filter((v) => v !== val));
    } else {
      onChange([...values, val]);
    }
  };

  const removeValue = (val: string) => {
    onChange(values.filter((v) => v !== val));
  };

  return (
    <div ref={ref} className="relative">
      {!hideLabel && label && (
        <label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1.5">
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      {/* Trigger */}
      <div
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`w-full px-3.5 py-1.5 border rounded-lg flex items-center min-h-[34px] transition-colors ${
          disabled
            ? "bg-gray-50 border-gray-100 cursor-not-allowed"
            : open
              ? "border-[#830051] bg-white cursor-pointer"
              : "border-gray-200 bg-white hover:border-gray-300 cursor-pointer"
        }`}
      >
        <div className="flex-1 flex flex-wrap items-center gap-1.5 min-w-0">
          {values.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 text-xs bg-pink-50 rounded-md px-2 py-0.5 text-gray-700 border border-pink-100"
            >
              {v}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeValue(v);
                }}
                disabled={disabled}
                className="w-3.5 h-3.5 flex items-center justify-center rounded hover:bg-pink-100 cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-[10px] text-gray-500"></i>
              </button>
            </span>
          ))}
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={values.length === 0 ? placeholder : ""}
            className="flex-1 min-w-0 w-0 text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400"
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canCreate) {
                e.preventDefault();
                toggleValue(search.trim());
                setSearch("");
              } else if (e.key === "Escape") {
                setOpen(false);
                setSearch("");
              }
            }}
          />
        </div>
        <i
          className={`ri-arrow-down-s-line text-gray-400 flex-shrink-0 ml-2 transition-transform ${open ? "rotate-180" : ""}`}
        ></i>
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg z-20 overflow-hidden"
          style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}
        >
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 && !canCreate && (
              <div className="px-3 py-2.5 text-xs text-gray-400">No options available</div>
            )}
            {filtered.map((opt) => {
              const selected = values.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => toggleValue(opt)}
                  className={`w-full px-3 py-2 text-sm text-left cursor-pointer hover:bg-pink-50 transition-colors flex items-center gap-2 ${
                    selected ? "text-[#830051] font-medium" : "text-gray-700"
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                      selected ? "border-[#830051] bg-[#830051]" : "border-gray-300 bg-white"
                    }`}
                  >
                    {selected && <i className="ri-check-line text-white text-[10px]"></i>}
                  </span>
                  <span className="flex-1">{opt}</span>
                </button>
              );
            })}

            {/* Create new */}
            {canCreate && (
              <button
                type="button"
                onClick={() => {
                  toggleValue(search.trim());
                  setSearch("");
                }}
                className="w-full px-3 py-2.5 text-sm text-left cursor-pointer hover:bg-pink-50 transition-colors border-t border-gray-100 flex items-center gap-2"
              >
                <i className="ri-add-line text-sm" style={{ color: "#830051" }}></i>
                <span className="text-gray-600">Create &ldquo;</span>
                <span className="font-semibold" style={{ color: "#830051" }}>
                  {search.trim()}
                </span>
                <span className="text-gray-600">&rdquo;</span>
              </button>
            )}
          </div>
          {values.length > 0 && (
            <div className="px-3 py-1.5 border-t border-gray-100 text-[11px] text-gray-400">
              {values.length} selected
            </div>
          )}
        </div>
      )}
    </div>
  );
}
